import { WaqLink } from "../../../ui/waq/WaqLink";
import { useRef, useState } from "react";
import { industries, industriesSection, type Industry } from "../../../../content/waq/industries";

/**
 * design/Homepage.dc.html lines 275-345. First light section.
 *
 * Carries the dark -> light handoff: a 140px navy-to-transparent gradient over
 * the field colour. That gradient is the only thing marking the boundary — the
 * no-full-bleed-border rule still applies.
 *
 * The reference tabs are <div onClick> with no keyboard path. Built as a
 * vertical ARIA tablist. All three panels render, hidden with display:none, so
 * their copy stays in the served HTML.
 */
export function Industries() {
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const select = (i: number) => {
    setActive(i);
    tabRefs.current[i]?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const last = industries.length - 1;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        select(active === last ? 0 : active + 1);
        break;
      case "ArrowUp":
        e.preventDefault();
        select(active === 0 ? last : active - 1);
        break;
      case "Home":
        e.preventDefault();
        select(0);
        break;
      case "End":
        e.preventDefault();
        select(last);
        break;
    }
  };

  return (
    <section
      className="bg-field bg-dark-to-light"
      aria-labelledby="industries-heading"
    >
      <div className="mx-auto max-w-container px-section-x py-section-y">
        <div className="mb-section-head-sm motion-safe:animate-rise">
          <p className="mb-3.5 text-eyebrow font-extrabold uppercase text-blue">
            {industriesSection.eyebrow}
          </p>
          <h2
            id="industries-heading"
            className="max-w-[720px] text-h2-light font-extrabold"
          >
            {industriesSection.heading}
          </h2>
        </div>

        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(300px,100%),1fr))] items-start gap-ind motion-safe:animate-rise">
          <div
            role="tablist"
            aria-orientation="vertical"
            aria-label={industriesSection.eyebrow}
            onKeyDown={onKeyDown}
            className="grid gap-2.5"
          >
            {industries.map((industry, i) => (
              <button
                key={industry.name}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                type="button"
                role="tab"
                id={`industry-tab-${i}`}
                aria-selected={active === i}
                aria-controls={`industry-panel-${i}`}
                tabIndex={active === i ? 0 : -1}
                onClick={() => setActive(i)}
                className={`cursor-pointer rounded-card border py-5 text-left transition-colors duration-300 ${
                  active === i
                    ? "border-navy border-l-[5px] border-l-yellow bg-navy pl-[18px] pr-[22px] text-surface"
                    : "border-hair bg-surface px-[22px] text-navy"
                }`}
              >
                <span className="block text-label-mid font-extrabold tabular-nums opacity-60">
                  {industry.number}
                </span>
                <span className="mt-2 block text-ind-title font-extrabold">
                  {industry.name}
                </span>
                <span className="mt-1.5 block text-ind-sub opacity-72">
                  {industry.tabDetail}
                </span>
              </button>
            ))}
          </div>

          <div className="rounded-panel bg-surface p-section-head-sm shadow-card">
            {industries.map((industry, i) => (
              <IndustryPanel
                key={industry.number}
                industry={industry}
                index={i}
                active={active === i}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function IndustryPanel({
  industry,
  index,
  active,
}: {
  industry: Industry;
  index: number;
  active: boolean;
}) {
  return (
    <div
      role="tabpanel"
      id={`industry-panel-${index}`}
      aria-labelledby={`industry-tab-${index}`}
      tabIndex={0}
      className={active ? "block motion-safe:animate-rise-panel" : "hidden"}
    >
      <h3 className="mb-3.5 text-h3 font-extrabold">{industry.name}</h3>

      <p className="mb-[26px] max-w-[620px] text-body-lg text-soft">{industry.body}</p>

      <ul className="mb-7 grid list-none gap-3">
        {industry.checks.map((check) => (
          <li
            key={check}
            className="flex items-start gap-3 text-check-item font-semibold"
          >
            <span
              aria-hidden="true"
              className="flex h-5 w-5 flex-none items-center justify-center rounded-md bg-yellow text-[11px] font-extrabold text-navy"
            >
              ✓
            </span>
            <span>{check}</span>
          </li>
        ))}
      </ul>

      <ul className="flex list-none flex-wrap gap-[9px] border-t border-hair pt-6">
        {industry.tags.map((tag) => (
          <li
            key={tag}
            className="rounded-pill border border-hair px-[13px] py-[7px] text-tag font-bold"
          >
            {tag}
          </li>
        ))}
      </ul>

      <WaqLink
        href={industry.link.href}
        className="mt-[26px] inline-flex items-center gap-2.5 text-nav font-extrabold text-blue"
      >
        {industry.link.label} <span aria-hidden="true">→</span>
      </WaqLink>
    </div>
  );
}
